export const CONCERN_GROUP_CATEGORIES = [
	"皮膚・被毛",
	"耳",
	"免疫・アレルギー",
	"お腹・腸",
	"口腔",
	"関節・運動器",
	"体重・代謝",
	"泌尿器",
	"肝臓・膵臓",
	"心臓",
	"呼吸器",
	"目",
	"脳・神経",
	"シニア",
	"ストレス・メンタル",
	"その他",
] as const;

export const CONCERN_GROUPS = [
	{
		category: "皮膚・被毛",
		details: [
			"アトピー性皮膚炎",
			"膿皮症",
			"マラセチア皮膚炎",
			"脂漏症",
			"乾燥肌",
			"フケ",
			"かゆみ",
			"脱毛",
			"趾間炎",
			"ホットスポット（急性湿性皮膚炎）",
			"毛艶の低下",
			"赤み",
		],
	},
	{
		category: "耳",
		details: ["外耳炎", "耳垢", "耳の臭い", "耳血腫", "慢性外耳炎中耳炎"],
	},
	{
		category: "免疫・アレルギー",
		details: [
			"アレルギー体質",
			"花粉症様症状",
			"免疫バランスの乱れ",
			"慢性炎症",
			"自己免疫疾患",
			"季節性のかゆみ",
		],
	},
	{
		category: "お腹・腸",
		details: [
			"軟便",
			"下痢",
			"便秘",
			"嘔吐",
			"お腹が弱い",
			"ガスが多い",
			"炎症性腸疾患（IBD）",
			"食物アレルギー",
			"胃腸炎",
			"食欲不振",
			"消化不良",
		],
	},
	{ category: "口腔", details: ["歯石", "歯周病", "口臭"] },
	{
		category: "関節・運動器",
		details: [
			"関節炎",
			"変形性関節症",
			"膝蓋骨脱臼（パテラ）",
			"股関節形成不全",
			"椎間板ヘルニア",
			"足を引きずる",
			"階段を嫌がる",
			"ジャンプしない",
			"シニア犬の運動機能低下",
		],
	},
	{
		category: "体重・代謝",
		details: ["肥満", "痩せ", "筋肉量低下", "糖尿病", "高脂血症"],
	},
	{
		category: "泌尿器",
		details: [
			"膀胱炎",
			"尿路結石",
			"頻尿",
			"血尿",
			"尿漏れ",
			"腎臓病",
			"慢性腎不全",
			"飲水量増加",
		],
	},
	{
		category: "肝臓・膵臓",
		details: ["肝機能低下", "脂肪肝", "胆泥症", "膵炎"],
	},
	{
		category: "心臓",
		details: ["僧帽弁閉鎖不全症", "心雑音", "心不全", "咳が出る", "息切れ"],
	},
	{
		category: "呼吸器",
		details: [
			"気管虚脱",
			"短頭種気道症候群",
			"慢性気管支炎",
			"咳",
			"いびき",
			"呼吸が荒い",
		],
	},
	{
		category: "目",
		details: [
			"涙やけ",
			"ドライアイ",
			"結膜炎",
			"白内障",
			"緑内障",
			"目やに",
			"視力低下",
		],
	},
	{
		category: "脳・神経",
		details: ["てんかん", "認知症", "前庭疾患", "ふらつき", "旋回行動"],
	},
	{
		category: "シニア",
		details: [
			"認知機能低下",
			"睡眠リズムの乱れ",
			"夜鳴き",
			"筋力低下",
			"食欲低下",
		],
	},
	{
		category: "ストレス・メンタル",
		details: [
			"分離不安",
			"不安症",
			"吠え",
			"雷恐怖症",
			"来客恐怖",
			"ストレス性下痢",
			"常同行動",
		],
	},
	{ category: "その他", details: ["その他"] },
] as const;

export type ConcernGroupCategory = (typeof CONCERN_GROUP_CATEGORIES)[number];
export type ConcernCategory =
	(typeof CONCERN_GROUPS)[number]["details"][number];

export const CONCERN_CATEGORIES = CONCERN_GROUPS.flatMap(
	(group) => group.details,
) as ConcernCategory[];

const legacyConcernMap = {
	皮膚トラブル: "その他",
	アレルギー: "その他",
	消化器: "その他",
	"関節・骨": "その他",
	"目・耳": "その他",
	環境アレルギー: "アレルギー体質",
	痒み: "かゆみ",
	お腹のお悩み: "お腹が弱い",
} as const satisfies Record<string, ConcernCategory>;

const concernGroupSet = new Set<string>(CONCERN_GROUP_CATEGORIES);
const concernDetailSet = new Set<string>(CONCERN_CATEGORIES);
const detailToGroup = new Map<string, ConcernGroupCategory>(
	CONCERN_GROUPS.flatMap((group) =>
		group.details.map((detail) => [detail, group.category] as const),
	),
);

export function normalizeConcernDetail(value: string): ConcernCategory | null {
	if (concernDetailSet.has(value)) return value as ConcernCategory;
	return legacyConcernMap[value as keyof typeof legacyConcernMap] ?? null;
}

function collectConcernGroups(
	values: readonly string[],
): ConcernGroupCategory[] {
	const seen = new Set<ConcernGroupCategory>();
	for (const value of values) {
		if (concernGroupSet.has(value)) {
			seen.add(value as ConcernGroupCategory);
			continue;
		}
		const normalized = normalizeConcernDetail(value);
		const group = normalized ? detailToGroup.get(normalized) : null;
		if (group) seen.add(group);
	}
	return [...seen];
}

export function concernGroupsForDetails(
	values: readonly string[],
): ConcernGroupCategory[] {
	const groups = collectConcernGroups(values);
	return groups.length > 0 ? groups : ["その他"];
}

/** Condition-detail UI vocab tied to selected major groups (not pet concern details). */
export const CONDITION_DETAIL_MAJOR_GROUPS = [
	"皮膚・被毛",
	"お腹・腸",
	"その他",
] as const;

export type ConditionDetailMajorGroup =
	(typeof CONDITION_DETAIL_MAJOR_GROUPS)[number];

export const SKIN_CONDITION_DETAILS = [
	"痒み",
	"赤み",
	"脱毛",
	"フケ",
	"ベタベタ",
	"乾燥",
	"ブツブツ",
	"ただれ",
	"皮膚・被毛：その他",
	"皮膚・被毛：異常なし",
] as const;

export const GUT_CONDITION_DETAILS = [
	"下痢軟便",
	"便秘",
	"血便",
	"粘膜便",
	"嘔吐",
	"おなら多め",
	"ゲップ多い",
	"腹痛",
	"お腹・腸：その他",
	"お腹・腸：異常なし",
] as const;

export const CONDITION_DETAILS = [
	...SKIN_CONDITION_DETAILS,
	...GUT_CONDITION_DETAILS,
	"その他：異常なし",
] as const;

export const CONDITION_NONE_BY_GROUP = {
	"皮膚・被毛": "皮膚・被毛：異常なし",
	"お腹・腸": "お腹・腸：異常なし",
	その他: "その他：異常なし",
} as const;

export const CONDITION_OTHER_BY_GROUP = {
	"皮膚・被毛": "皮膚・被毛：その他",
	"お腹・腸": "お腹・腸：その他",
} as const;

export function conditionDetailLabel(detail: string): string {
	if (detail.endsWith("：異常なし")) return "異常なし";
	return detail.endsWith("：その他") ? "その他" : detail;
}

export function conditionDetailGroup(
	detail: string,
): ConditionDetailMajorGroup | null {
	if ((SKIN_CONDITION_DETAILS as readonly string[]).includes(detail))
		return "皮膚・被毛";
	if ((GUT_CONDITION_DETAILS as readonly string[]).includes(detail))
		return "お腹・腸";
	if (detail === CONDITION_NONE_BY_GROUP.その他) return "その他";
	return null;
}

/** Same-category symptoms and explicit none are mutually exclusive. */
export function toggleConditionDetail(
	current: readonly ConditionDetail[],
	detail: ConditionDetail,
): ConditionDetail[] {
	if (current.includes(detail))
		return current.filter((value) => value !== detail);
	const group = conditionDetailGroup(detail);
	const none = group ? CONDITION_NONE_BY_GROUP[group] : null;
	return [
		...current.filter((value) =>
			detail === none ? conditionDetailGroup(value) !== group : value !== none,
		),
		detail,
	];
}

export type ConditionDetail = (typeof CONDITION_DETAILS)[number];

export const CONDITION_OTHER_TEXT_MAX_LENGTH = 200;

const conditionDetailSet = new Set<string>(CONDITION_DETAILS);
const skinConditionDetailSet = new Set<string>(SKIN_CONDITION_DETAILS);
const gutConditionDetailSet = new Set<string>(GUT_CONDITION_DETAILS);

export function isConditionDetail(value: string): value is ConditionDetail {
	return conditionDetailSet.has(value);
}

/**
 * Registered groups used for record-summary eligibility and symptom bonuses.
 * 皮膚・被毛 / お腹・腸 stay as-is; any other selected concern major
 * (including explicit その他) collapses to その他. その他 is also available
 * alongside skin/gut. Empty concerns → [].
 * Return order follows CONDITION_DETAIL_MAJOR_GROUPS.
 */
export function conditionDetailMajorGroupsForConcerns(
	values: readonly string[],
): ConditionDetailMajorGroup[] {
	const groups = collectConcernGroups(values);
	if (groups.length === 0) return [];

	const selected = new Set<ConditionDetailMajorGroup>(["その他"]);
	for (const group of groups) {
		if (group === "皮膚・被毛" || group === "お腹・腸") {
			selected.add(group);
		} else {
			selected.add("その他");
		}
	}
	return CONDITION_DETAIL_MAJOR_GROUPS.filter((group) => selected.has(group));
}

export function conditionDetailsForMajorGroups(
	groups: readonly ConditionDetailMajorGroup[],
): ConditionDetail[] {
	const details: ConditionDetail[] = [];
	if (groups.includes("皮膚・被毛")) {
		details.push(...SKIN_CONDITION_DETAILS);
	}
	if (groups.includes("お腹・腸")) {
		details.push(...GUT_CONDITION_DETAILS);
	}
	if (groups.includes("その他")) details.push(CONDITION_NONE_BY_GROUP.その他);
	return details;
}

export function isConditionDetailAllowedForMajorGroups(
	detail: string,
	groups: readonly ConditionDetailMajorGroup[],
): detail is ConditionDetail {
	if (!isConditionDetail(detail)) return false;
	if (groups.includes("皮膚・被毛") && skinConditionDetailSet.has(detail)) {
		return true;
	}
	if (groups.includes("お腹・腸") && gutConditionDetailSet.has(detail)) {
		return true;
	}
	if (groups.includes("その他") && detail === CONDITION_NONE_BY_GROUP.その他)
		return true;
	return false;
}

/**
 * Labels to render immediately beside a major-concern chip on the public feed.
 * Skin/gut → selected detail chips; その他 → free text when present.
 */
export function conditionDetailLabelsBesideConcern(input: {
	concern: string;
	conditionDetails: readonly string[];
	conditionOtherText?: string;
	conditionSkinOtherText?: string;
	conditionGutOtherText?: string;
}): string[] {
	if (input.concern === "皮膚・被毛") {
		return input.conditionDetails
			.filter((detail) => skinConditionDetailSet.has(detail))
			.flatMap((detail) =>
				detail === CONDITION_OTHER_BY_GROUP["皮膚・被毛"]
					? input.conditionSkinOtherText?.trim()
						? [input.conditionSkinOtherText.trim()]
						: []
					: [conditionDetailLabel(detail)],
			);
	}
	if (input.concern === "お腹・腸") {
		return input.conditionDetails
			.filter((detail) => gutConditionDetailSet.has(detail))
			.flatMap((detail) =>
				detail === CONDITION_OTHER_BY_GROUP["お腹・腸"]
					? input.conditionGutOtherText?.trim()
						? [input.conditionGutOtherText.trim()]
						: []
					: [conditionDetailLabel(detail)],
			);
	}
	if (input.concern === "その他") {
		const text = input.conditionOtherText?.trim() ?? "";
		if (input.conditionDetails.includes(CONDITION_NONE_BY_GROUP.その他))
			return ["異常なし"];
		return text.length > 0 ? [text] : [];
	}
	return [];
}

export type NormalizeConditionDetailsInput = {
	conditionDetails: readonly string[];
	conditionOtherText: string;
	conditionSkinOtherText?: string;
	conditionGutOtherText?: string;
	majorGroups: readonly ConditionDetailMajorGroup[];
	snackCount: 0 | 1;
};

export type NormalizeConditionDetailsResult =
	| {
			conditionDetails: ConditionDetail[];
			conditionOtherText: string;
			conditionSkinOtherText?: string;
			conditionGutOtherText?: string;
			ok: true;
	  }
	| { message: string; ok: false };

/**
 * Server/client shared rules:
 * - snack_count=0 → details and other text must be empty
 * - details must belong to the allowed current or saved major groups
 * - category other text requires its selected marker (max 200 each)
 * - general other text requires その他; no groups requires all fields empty
 */
export function normalizeConditionDetailsInput(
	input: NormalizeConditionDetailsInput,
): NormalizeConditionDetailsResult {
	const otherText = input.conditionOtherText.trim();
	const categoryTexts = [
		{ group: "皮膚・被毛", text: input.conditionSkinOtherText?.trim() ?? "" },
		{ group: "お腹・腸", text: input.conditionGutOtherText?.trim() ?? "" },
	] as const;
	const uniqueDetails: ConditionDetail[] = [];
	const seen = new Set<string>();

	for (const value of input.conditionDetails) {
		if (seen.has(value)) continue;
		seen.add(value);
		if (!isConditionDetail(value)) {
			return {
				message: "未対応の調子詳細です。",
				ok: false,
			};
		}
		uniqueDetails.push(value);
	}

	if (input.snackCount === 0) {
		if (
			uniqueDetails.length > 0 ||
			otherText.length > 0 ||
			categoryTexts.some(({ text }) => text.length > 0)
		) {
			return {
				message: "与えなかった記録では調子の詳細を残せません。",
				ok: false,
			};
		}
		return {
			conditionDetails: [],
			conditionOtherText: "",
			...(input.conditionSkinOtherText !== undefined
				? { conditionSkinOtherText: "" }
				: {}),
			...(input.conditionGutOtherText !== undefined
				? { conditionGutOtherText: "" }
				: {}),
			ok: true,
		};
	}

	const allowed = new Set(conditionDetailsForMajorGroups(input.majorGroups));
	for (const detail of uniqueDetails) {
		if (!allowed.has(detail)) {
			return {
				message: "ペットの悩みカテゴリに合わない調子詳細です。",
				ok: false,
			};
		}
	}

	for (const { group, text } of categoryTexts) {
		if (text.length > CONDITION_OTHER_TEXT_MAX_LENGTH)
			return {
				ok: false,
				message: `${group}のその他は${CONDITION_OTHER_TEXT_MAX_LENGTH}文字以内で入力してください。`,
			};
		const selected = uniqueDetails.includes(CONDITION_OTHER_BY_GROUP[group]);
		if (selected && !text)
			return {
				ok: false,
				message: `${group}のその他の内容を入力してください。`,
			};
		if (text && (!selected || !input.majorGroups.includes(group)))
			return { ok: false, message: `${group}のその他を選択してください。` };
	}
	const allowsOther = input.majorGroups.includes("その他");
	for (const group of CONDITION_DETAIL_MAJOR_GROUPS) {
		if (
			uniqueDetails.includes(CONDITION_NONE_BY_GROUP[group]) &&
			(uniqueDetails.some(
				(detail) =>
					conditionDetailGroup(detail) === group &&
					detail !== CONDITION_NONE_BY_GROUP[group],
			) ||
				(group === "その他" && otherText.length > 0))
		)
			return {
				message: "同じカテゴリの症状と異常なしは同時に選択できません。",
				ok: false,
			};
	}
	if (!allowsOther && otherText.length > 0) {
		return {
			message: "その他の悩みがないペットでは自由記述を残せません。",
			ok: false,
		};
	}
	if (otherText.length > CONDITION_OTHER_TEXT_MAX_LENGTH) {
		return {
			message: `その他の詳細は${CONDITION_OTHER_TEXT_MAX_LENGTH}文字以内で入力してください。`,
			ok: false,
		};
	}

	if (
		input.majorGroups.length === 0 &&
		(uniqueDetails.length > 0 || otherText.length > 0)
	) {
		return {
			message: "このペットでは調子の詳細を残せません。",
			ok: false,
		};
	}

	return {
		conditionDetails: uniqueDetails,
		conditionOtherText: allowsOther ? otherText : "",
		...(input.conditionSkinOtherText !== undefined
			? { conditionSkinOtherText: categoryTexts[0].text }
			: {}),
		...(input.conditionGutOtherText !== undefined
			? { conditionGutOtherText: categoryTexts[1].text }
			: {}),
		ok: true,
	};
}
